import React, { useCallback, useEffect, useMemo, FC, ReactNode } from "react";

import classNames from "classnames";

import {
  StyledOptionGroup,
  StyledOptionGroupHeader,
  StyledOptionGroupOption,
  StyledOptionGroupLi,
  StyledOptionGroupHeaderName,
} from "./Styles";
import { Checkbox } from "../../Checkbox/Checkbox";
import { IOptionItemProps } from "../Option/Option";

export interface IOptionGroupProps extends IOptionItemProps {
  /** Options items list */
  options: IOptionItemProps[];
}

interface IAdditionalOptionGroupProps {
  /** Select type: single or multiple */
  type?: "single" | "multiple";
  /** Value of hovered element */
  hover?: number | string | null;
  /** Filter for labels */
  filter?: string;
  /** Checked values */
  checked: (string | number)[];
  /** Mixed options values */
  mixedOptions: (string | number)[];
  /** onChange callback */
  onChange?: (e: (string | number)[]) => void;
  /** Set value of hovered element */
  setHover?: (e: number | string | undefined) => void;
  /** Render function */
  renderOptions?: (item: IOptionItemProps, index: number) => ReactNode;
  /** Value that represent class names */
  optionGroupClassName?: string;
}

export const OptionGroup: FC<
  IOptionGroupProps & IAdditionalOptionGroupProps
> = ({
  id,
  name,
  options,
  type,
  hover,
  checked,
  onChange,
  setHover,
  optionGroupClassName,
  mixedOptions = [],
}) => {
  useEffect(() => {
    if (hover === id && type !== "single") {
      document.getElementById(`${id}`)?.focus();
    }
  }, [hover, type, id]);

  const isAllOptionsChecked = useMemo(
    () => options.every((option) => checked.includes(option.id)),
    [options, checked]
  );

  const handleHover = useCallback(() => {
    if (setHover) {
      setHover(type === "single" ? undefined : id);
    }
  }, [setHover, type, id]);

  const allOptionValues = useMemo(
    () => options.map((option) => option.id),
    [options]
  );

  const isMixed: boolean = useMemo(() => {
    return (
      (options.some((option) => checked.includes(option.id)) ||
        mixedOptions.some((item) => allOptionValues.includes(item))) &&
      !isAllOptionsChecked
    );
  }, [allOptionValues, checked, isAllOptionsChecked, mixedOptions, options]);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (onChange && type !== "single") {
        if (isAllOptionsChecked) {
          onChange(
            checked.filter((i) => allOptionValues.includes(i) === false)
          );
        } else {
          const values = [...checked, ...allOptionValues];
          const unique = values.filter((item, pos) => {
            return values.indexOf(item) === pos;
          });
          onChange(unique);
        }
      }
    },
    [allOptionValues, checked, isAllOptionsChecked, onChange, type]
  );

  return (
    <StyledOptionGroupLi
      key={id}
      type={type}
      className={classNames("c-option-group", optionGroupClassName)}
      onClick={handleClick}
    >
      <StyledOptionGroup key={id}>
        <StyledOptionGroupOption
          onMouseEnter={handleHover}
          hover={hover === id && type === "multiple"}
        >
          <StyledOptionGroupHeader>
            <StyledOptionGroupHeaderName weight="bold">
              {name}
            </StyledOptionGroupHeaderName>
            {type === "multiple" && (
              <Checkbox
                id={`${id}`}
                checked={isAllOptionsChecked}
                mixed={isMixed}
                onChange={handleClick}
                type="checkbox"
              />
            )}
          </StyledOptionGroupHeader>
        </StyledOptionGroupOption>
      </StyledOptionGroup>
    </StyledOptionGroupLi>
  );
};

OptionGroup.displayName = "OptionGroup";
